// Header.jsx — шапка админпанели v2.
//
// Слева: заголовок страницы (зависит от текущего роута).
// Справа: профиль с выпадающим меню — Мой аккаунт, Настройки, Выход.
//
// Заголовок выбирается по карте { pathname → text }, чтобы не плодить
// длинную лестницу if/else.

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, NavLink } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import cl from './Header.module.css';
import HeaderUser from '../UI/HeaderUser/HeaderUser';
import { IconUser, IconSettings, IconLogout, IconChevronDown, IconMenu } from '../Icons';
import logger from '../../utils/logger';

// Карта роутов → заголовков. exact-сравнение, потом — префиксное.
const TITLES = {
    '/main': 'Дашборд',
    '/main/students': 'Ученики',
    '/main/jornal': 'Расписание занятий',
    '/main/jornal/settings': 'Настройки журнала',
    '/main/edit-courses': 'Редактирование курсов',
    '/main/edit-events': 'Редактирование мероприятий',
    '/main/pays': 'Платежи',
    '/main/events': 'Мероприятия',
    '/main/accountStudents': 'Подключение аккаунтов',
    '/main/prepodandadmin': 'Преподаватели и админы',
    '/main/aproveproject': 'Проверка проектов',
    '/main/progressstudent': 'Прогресс учеников',
    '/main/paysacount': 'Счета на оплату',
    '/main/materials': 'Статьи и материалы',
    '/main/me': 'Мой аккаунт',
    '/main/settings': 'Настройки аккаунта',
};

const TITLE_PREFIXES = [
    { prefix: '/main/students/', title: 'Карточка ученика' },
    { prefix: '/main/journal/course/', title: 'Журнал курса' },
    { prefix: '/main/events/event/', title: 'Журнал мероприятия' },
    { prefix: '/main/articles/', title: 'Статьи' },
    { prefix: '/main/faq/', title: 'FAQ' },
    { prefix: '/main/forum/', title: 'Форум' },
];

function getTitle(pathname) {
    if (TITLES[pathname]) return TITLES[pathname];
    for (const p of TITLE_PREFIXES) {
        if (pathname.startsWith(p.prefix)) return p.title;
    }
    return 'СтартКласс';
}


const Header = ({ onMenuToggle, showMenuButton }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { isAuthenticated, accessRole, logout, isLoading } = useAuth();
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const profileRef = useRef(null);

    // Заголовок текущей страницы. useMemo чтобы не пересчитывать на каждый рендер.
    const pageTitle = useMemo(() => getTitle(location.pathname), [location.pathname]);

    // Закрытие меню при клике вне его.
    useEffect(() => {
        const onDocClick = (e) => {
            if (profileRef.current && !profileRef.current.contains(e.target)) {
                setIsProfileOpen(false);
            }
        };
        if (isProfileOpen) {
            document.addEventListener('mousedown', onDocClick);
            return () => document.removeEventListener('mousedown', onDocClick);
        }
    }, [isProfileOpen]);

    // Закрываем меню при смене страницы.
    useEffect(() => {
        setIsProfileOpen(false);
    }, [location.pathname]);

    const handleLogout = async () => {
        if (isLoggingOut) return;
        try {
            setIsLoggingOut(true);
            setIsProfileOpen(false);
            logout();
            navigate('/login');
        } catch (e) {
            logger.error('Ошибка выхода:', e);
        } finally {
            setTimeout(() => setIsLoggingOut(false), 500);
        }
    };

    // Пока AuthContext грузится — пустой header (чтобы не моргнул контент).
    if (isLoading) {
        return <header className={cl.header} aria-busy="true"/>;
    }

    // Текст роли — без эмодзи.
    const roleLabel = {
        owner: 'Руководитель',
        teacher: 'Преподаватель',
        admin: 'Администратор',
    }[accessRole] || 'Пользователь';

    return (
        <header className={cl.header}>
            <div className={cl.inner}>
                {/* Слева: бургер (мобила) + заголовок страницы */}
                <div className={cl.titleBlock}>
                    {showMenuButton && (
                        <button
                            type="button"
                            className={cl.burger}
                            onClick={onMenuToggle}
                            aria-label="Открыть меню"
                        >
                            <IconMenu width={20} height={20}/>
                        </button>
                    )}
                    <h1 className={cl.title}>{pageTitle}</h1>
                </div>

                {/* Справа: профиль */}
                <div className={cl.rightSection}>
                    {isAuthenticated ? (
                        <div className={cl.profile} ref={profileRef}>
                            <button
                                className={cl.profileButton}
                                onClick={() => setIsProfileOpen(o => !o)}
                                aria-expanded={isProfileOpen}
                                aria-label="Меню профиля"
                            >
                                <HeaderUser />
                                <span className={`${cl.chevron} ${isProfileOpen ? cl.chevronOpen : ''}`}>
                                    <IconChevronDown width={16} height={16}/>
                                </span>
                            </button>

                            {isProfileOpen && (
                                <div className={cl.dropdown}>
                                    <div className={cl.dropdownHeader}>
                                        <div className={cl.dropdownRole}>{roleLabel}</div>
                                    </div>

                                    <NavLink to="/main/me" className={cl.dropdownItem}>
                                        <IconUser width={16} height={16}/>
                                        <span>Мой аккаунт</span>
                                    </NavLink>

                                    <NavLink to="/main/settings" className={cl.dropdownItem}>
                                        <IconSettings width={16} height={16}/>
                                        <span>Настройки</span>
                                    </NavLink>

                                    <div className={cl.dropdownDivider}/>

                                    <button
                                        className={`${cl.dropdownItem} ${cl.dropdownItemDanger}`}
                                        onClick={handleLogout}
                                        disabled={isLoggingOut}
                                    >
                                        <IconLogout width={16} height={16}/>
                                        <span>{isLoggingOut ? 'Выход…' : 'Выйти'}</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : null}
                </div>
            </div>
        </header>
    );
};

export default Header;
